import { useState, useCallback, useEffect, useRef } from "react";
import GoogleMapReact from "google-map-react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { mapAction } from "redux/slices/map/mapSlice";
import { change } from "redux-form";
import { useTranslation } from "react-i18next";
import newPin from "../../../assets/icons/pin.svg";
import plus from "../../../assets/icons/plus.svg";
import minus from "../../../assets/icons/minus.svg";
import mark from "../../../assets/icons/markblack.svg";
import { parseAddressFromLatLng } from "utils/location.utils";

const MapWithPin = () => {
  const { t } = useTranslation("translation");
  const dispatch = useAppDispatch();

  // Dynamically determine which form is active based on the tab
  const tab = useAppSelector((state)=> state.booking.tab);
  const form = tab === 3 ? "DeliveryForm" : "PickUpForm";
  const [pickupAddress, setPickupAddress] = useState<any>(null);
  const [destinationAddress, setDestinationAddress] = useState<any>(null);
  const [mapApi, setMapApi] = useState<any>(null);
  const [mapsApi, setMapsApi] = useState<any>(null);
  const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isFading, setIsFading] = useState(false);

  const hasInteractedRef = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const currentLocation = useAppSelector((state) => state.map.startLocation);
  const startFormatted = useAppSelector((state) => state.map.startformatted);
  const endFormatted = useAppSelector((state) => state.map.endformatted);

  const picklatlong = useAppSelector(
    (state) => state.form?.[form]?.values?.pickup?.details?.geometry?.location
  );
  const destinationlatlong = useAppSelector(
    (state) =>
      state.form?.[form]?.values?.destination?.details?.geometry?.location
  );

  const pickup = useAppSelector((state) => state.form?.[form]?.values?.pickup);
  const destination = useAppSelector(
    (state) => state.form?.[form]?.values?.destination
  );

  let endLat = useAppSelector((state) => state.map.endLat);
  let endlng = useAppSelector((state) => state.map.endlng);

  const show = useAppSelector((state) => state.map.show);
  const openedFromCurrentLocation = useAppSelector(
    (state) => state.map.openedFromCurrentLocation
  );

  const isSmallScreen = window.innerWidth < 768;

  const hideBanner = useCallback(() => {
    if (!hasInteractedRef.current) {
      setIsFading(true);
      setTimeout(() => setShowBanner(false), 300);
      hasInteractedRef.current = true;
    }
  }, []);
  // Auto-hide after 4 seconds
  useEffect(() => {
    const timer = setTimeout(hideBanner, 4000);
    return () => clearTimeout(timer);
  }, [hideBanner]);

  const handleApiLoaded = useCallback((map: any, maps: any) => {
    setMapApi(map);
    setMapsApi(maps);

    if (map && typeof map.setZoom === "function") {
      map.setZoom(16);
    }
  }, []);

  const getMapCenter = () => {
    if (show === 0) {
      if (currentLocation && Object.keys(currentLocation).length > 0) {
        // @ts-ignore
        return { lat: currentLocation.lat, lng: currentLocation.lng };
      }
      if (picklatlong?.lat && picklatlong?.lng) {
        return { lat: picklatlong.lat, lng: picklatlong.lng };
      }
    }
    if (show === 1) {
      if (currentLocation && Object.keys(currentLocation).length > 0) {
        // @ts-ignore
        return { lat: currentLocation.lat, lng: currentLocation.lng };
      }
      if (destinationlatlong?.lat && destinationlatlong?.lng) {
        return { lat: destinationlatlong.lat, lng: destinationlatlong.lng };
      }
      return { lat: 24.7191924, lng: 46.6722224 };
    }
    return { lat: 24.7191924, lng: 46.6722224 };
  };

  useEffect(() => {
    if (!mapApi) return;

    const center = getMapCenter();
    if (!center || !center.lat || !center.lng) return;

    try {
      if (typeof mapApi.panTo === "function") {
        mapApi.panTo(center);
      } else if (mapApi.setCenter) {
        mapApi.setCenter(center);
      }

      if (typeof mapApi.setZoom === "function") {
        const currentZoom = mapApi.getZoom ? mapApi.getZoom() : null;

        if (!currentZoom || currentZoom < 15) {
          mapApi.setZoom(16);
        }
      }

      if (!pickup?.details || !destination?.details) {
        const newAddress = debouncedFetchAddress(center.lat, center.lng);
      }
    } catch (err) {
      console.error("Error panning map to new center:", err);
    }
  }, [
    mapApi,
    show,
    currentLocation,
    picklatlong,
    destinationlatlong,
    startFormatted,
    endFormatted,
    endLat,
    endlng,
  ]);

  // Get current address based on show state
  const getCurrentAddress = () => {
    return show === 0 ? pickupAddress : destinationAddress;
  };

  // Initialize address when map opens - both with current location and default coordinates
  useEffect(() => {
    const initializeAddress = async () => {
      if (mapApi) {
        const mapCenter = getMapCenter();

        if (mapCenter && mapCenter.lat && mapCenter.lng) {
          setIsLoadingAddress(true);
          try {
            if (!openedFromCurrentLocation && (pickup || destination)) {
              // if (!openedFromCurrentLocation && (pickup || destination)) {
              if (show === 0) {
                setPickupAddress(pickup);
              } else {
                setDestinationAddress(destination);
              }
            } else {
              // Fetch new address
              const mainAddress = await parseAddressFromLatLng(
                mapCenter.lat,
                mapCenter.lng
              );

              if (mainAddress) {
                // If it's current location, customize the display
                if (
                  currentLocation &&
                  Object.keys(currentLocation).length > 0
                ) {
                  const customizedAddress = {
                    ...mainAddress,
                    value: mainAddress.value || "Your Current Address",
                    details: {
                      ...mainAddress.details,
                      name: mainAddress.details?.name || "Current Location",
                    },
                  };

                  if (show === 0) {
                    setPickupAddress(customizedAddress);
                  } else {
                    setDestinationAddress(customizedAddress);
                  }
                } else {
                  // For default coordinates or other locations, use the address as is
                  if (show === 0) {
                    setPickupAddress(mainAddress);
                  } else {
                    setDestinationAddress(mainAddress);
                  }
                }
              }
            }
          } catch (error) {
            console.error("Error fetching initial address:", error);
          } finally {
            setIsLoadingAddress(false);
          }
        }
      }
    };

    initializeAddress();
  }, [
    mapApi,
    currentLocation,
    picklatlong,
    destinationlatlong,
    endLat,
    endlng,
    show,
    startFormatted,
    endFormatted,
  ]);

  useEffect(() => {
    if (show === 0) {
      setDestinationAddress(null);
    } else {
      setPickupAddress(null);
    }
  }, [show]);

  const debouncedFetchAddress = useCallback(
    async (lat: number, lng: number) => {
      try {
        setIsLoadingAddress(true);
        const mainAddress = await parseAddressFromLatLng(lat, lng);
        if (mainAddress) {
          if (show === 0) {
            setPickupAddress(mainAddress);
          } else {
            setDestinationAddress(mainAddress);
          }
        }
      } catch (error) {
        console.error("Error fetching address:", error);
      } finally {
        setIsLoadingAddress(false);
      }
    },
    [show]
  );

  const handleDrag = useCallback(async () => {
    hideBanner(); // Hide on first drag
    if (mapApi) {
      const center = mapApi.getCenter();
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      setIsLoadingAddress(true);
      debounceTimer.current = setTimeout(() => {
        debouncedFetchAddress(center.lat(), center.lng());
      }, 2000);
    }
  }, [mapApi, debouncedFetchAddress, hideBanner]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleConfirm = () => {
    const currentAddress = getCurrentAddress();

    if (currentAddress && !isLoadingAddress) {
      if (show === 0) {
        dispatch(
          mapAction.setStartLocation(currentAddress.details.geometry.location)
        );
        dispatch(mapAction.setStartFormatted(currentAddress));
        dispatch(change(form, "pickup", currentAddress));
      } else {
        dispatch(
          mapAction.setEndLocation(currentAddress.details.geometry.location)
        );
        dispatch(mapAction.setdestinationMap(false));
        dispatch(mapAction.setEndFormatted(currentAddress));
        dispatch(change(form, "destination", currentAddress));
      }
      dispatch(mapAction.setOpen(false));
      dispatch(mapAction.setClose(true));
    }
  };

  const handleZoomIn = () => {
    if (mapApi) {
      const currentZoom = mapApi.getZoom();
      mapApi.setZoom(currentZoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapApi) {
      const currentZoom = mapApi.getZoom();
      mapApi.setZoom(currentZoom - 1);
    }
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation && mapApi) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        mapApi.panTo({ lat: latitude, lng: longitude });
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        setIsLoadingAddress(true);
        const mainAddress = await parseAddressFromLatLng(latitude, longitude);
        if (mainAddress) {
          if (show === 0) {
            setPickupAddress(mainAddress);
          } else {
            setDestinationAddress(mainAddress);
          }
        }
        setIsLoadingAddress(false);
      });
    }
  };

  const mapOptions = {
    gestureHandling: isSmallScreen ? "greedy" : "auto",
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    styles: [
      // Map background
      {
        featureType: "all",
        elementType: "geometry",
        stylers: [{ color: "#E8EAF0" }],
      },
      // Roads - Main highways
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#7B9BC7" }, { weight: 3 }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry.stroke",
        stylers: [{ color: "#6B8BB7" }, { weight: 1 }],
      },
      // Roads - Major roads
      {
        featureType: "road.arterial",
        elementType: "geometry",
        stylers: [{ color: "#A8B8D8" }, { weight: 2 }],
      },
      // Roads - Local roads
      {
        featureType: "road.local",
        elementType: "geometry",
        stylers: [{ color: "#C5D1E8" }, { weight: 1 }],
      },
      // Water bodies
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#B8CAE8" }],
      },
      // Parks and green areas
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#DDE2ED" }],
      },
      // Buildings
      {
        featureType: "poi.business",
        elementType: "geometry",
        stylers: [{ color: "#D5DAE5" }],
      },
      // Labels - Main text
      {
        featureType: "all",
        elementType: "labels.text.fill",
        stylers: [{ color: "#5A6B7A" }],
      },
      {
        featureType: "all",
        elementType: "labels.text.stroke",
        stylers: [{ color: "#FFFFFF" }, { weight: 2 }],
      },
      // Administrative labels
      {
        featureType: "administrative",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4A5A6A" }],
      },
      // Hide unnecessary elements
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "simplified" }],
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "simplified" }],
      },
      // Road labels
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#4A5A6A" }],
      },
      // Landscape
      {
        featureType: "landscape",
        elementType: "geometry",
        stylers: [{ color: "#E8EAF0" }],
      },
      // Transit stations
      {
        featureType: "transit.station",
        elementType: "geometry",
        stylers: [{ color: "#C8D4E8" }],
      },
    ],
  };
  const displayAddress = getCurrentAddress();
  return (
    <div className="map-container">
      {/* Instruction Banner with fade effect */}
      {showBanner && (
        <div className={`instruction-banner ${isFading ? "fade-out" : ""}`}>
          {t("move_the_pin")}
        </div>
      )}
      {/* Map Controls */}
      <div className="map-controls">
        <button className="control-btn zoom-in" onClick={handleZoomIn}>
          <img src={plus} alt="plus" />
        </button>
        <button className="control-btn zoom-out" onClick={handleZoomOut}>
          <img src={minus} alt="minus" />
        </button>
      </div>
      {/* Current Location Button */}
      <button className="current-location-btn" onClick={handleCurrentLocation}>
        <div>
          <img src={mark} alt="black-mark" />
        </div>
      </button>
      {/* Pin Icon */}
      <img src={newPin} alt="pin" className="map-pin" />
      {/* Address Display */}
      <div className="address-display">
        <div className="address-text">
          {isLoadingAddress ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">
                <h3>{t("finding_location")}</h3>
                <p>{t("please_wait")}</p>
              </div>
            </div>
          ) : (
            <>
              <h3>{displayAddress?.details?.name || ""}</h3>
              <p>{displayAddress?.value || t("move_map_to_select")}</p>
            </>
          )}
        </div>
        <button
          className="confirm-btn"
          onClick={handleConfirm}
          disabled={!displayAddress || isLoadingAddress}
        >
          {isLoadingAddress ? t("loading") : t("confirm")}
        </button>
      </div>

      <GoogleMapReact
        center={getMapCenter()}
        options={mapOptions}
        defaultZoom={16}
        yesIWantToUseGoogleMapApiInternals={true}
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        onDragEnd={handleDrag}
        onZoomAnimationEnd={handleDrag}
      />

      <style>{`
        .map-container {
          position: relative;
          height: 100%;
          width: 100%;
          overflow: hidden;
          border-radius:16px;
          box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        }

        .instruction-banner {
          position: fixed;
          width:100px;
          top: 22%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 12px 8px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.4px;
          z-index: 1000;
          backdrop-filter: blur(10px);
          text-align: center;
        }

        .map-controls {
          position: absolute;
          top: 10px;
          right: 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 1000;
        }

        .control-btn {
          width: 40px;
          height: 40px;
          background: white;
          border: none;
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
          color: #333;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .control-btn:hover {
          background: #f5f5f5;
          transform: scale(1.05);
        }

        .control-btn:active {
          transform: scale(0.95);
        }

        .current-location-btn {
          position: fixed;
          bottom: 100px;
          right: 20px;
          width: 40px;
          height: 40px;
          background: white;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          transition: all 0.2s ease;
        }

        .current-location-btn:hover {
          background: #f5f5f5;
          transform: scale(1.05);
        }

        .current-location-btn:active {
          transform: scale(0.95);
        }

        .location-icon {
          width: 20px;
          height: 20px;
          border: 2px solid #333;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .location-dot {
          width: 6px;
          height: 6px;
          background: #333;
          border-radius: 50%;
        }

        .map-pin {
          position: fixed;
          top: 45%;
          left: 50%;
          transform: translate(-50%, -100%);
          z-index: 999;
          width: 40px;
          height: 40px;
        }

        .address-display {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #ffff;
          padding: 20px;
          z-index: 1000;
          display: flex;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .address-text {
          flex: 1;
          color: white;
          max-height: 45px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .address-text h3 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 500;
          color:black
        }

        .address-text p {
          margin: 0;
          font-size: 13.4px;
          opacity: 0.9;
          line-height: 1.3;
          font-weight: 500;
          color:#00000099 !important
        }

        .loading-container {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e0e0e0;
          border-left: 2px solid #520A76;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text h3 {
          margin: 0 0 2px 0;
          font-size: 14px;
          font-weight: 500;
          color: black;
        }

        .loading-text p {
          margin: 0;
          font-size: 12px;
          color: #00000099;
          font-weight: 400;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .confirm-btn {
          background: #520A76;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 80px;
        }

        .confirm-btn:hover {
          background: #6A1B9A;
          transform: translateY(-1px);
        }

        .confirm-btn:active {
          transform: translateY(0);
        }

        .confirm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .confirm-btn:disabled:hover {
          background: #520A76;
          transform: none;
        }

        @media screen and (max-width: 900px) {
          .map-container {
            width: calc(100% - 24px);
            height: calc(100% - 22px);
            margin: 10px 12px 12px;
            border-radius: 20px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
          }

          .instruction-banner {
            position: absolute;
            top: 35%;
            left: 50%;
            transform: translateX(-50%);
            font-size: 9px;
            padding: 10px 8px;
          }

          .map-controls {
            top: 10px;
            right: 10px;
          }

          .map-pin {
            position: absolute;
            top: 45%;
            left: 50%;
            transform: translate(-50%, -100%);
          }

          .current-location-btn {
            position: absolute;
            bottom: 156px;
            right: 10px;
          }

          .address-display {
            position: absolute;
            border-top: 1px solid #E0E0E0;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            padding: 16px;
          }

          .address-text {
            max-height: none;
          }

          .loading-container {
            gap: 8px;
          }

          .confirm-btn {
            width: 100%;
            padding: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default MapWithPin;
